import { useState, useEffect, useRef } from 'react';
import type { AnalysisType } from '../../shared/types';
import { LoaderRing } from './ui/ScoreRing';

interface Props {
  analysisType: string;
  /** Clé de la typologie — acceptée pour l'appelant ; la teinte du panneau
      vient de card-tint, l'anneau lui reste celui du score final. */
  analysisKey?: AnalysisType;
  /** Progression réelle 0-100 émise par le backend en SSE. null = pas encore reçu. */
  progress?: number | null;
  /** Label correspondant à l'étape en cours côté backend. */
  progressLabel?: string | null;
}

// Fallback : steps animés en boucle si on n'a pas encore reçu d'event SSE.
const ANALYSIS_STEPS_FALLBACK = [
  'Analyse du contenu en cours...',
  'Extraction des éléments factuels...',
  'Recherche dans la base juridique...',
  'Évaluation par l\'avocat de la défense...',
  'Évaluation par le contradicteur...',
  'Arbitrage et pondération en cours...',
  'Rédaction du verdict final...',
];

const QUESTION_STEPS_FALLBACK = [
  'Recherche dans la base juridique...',
  'Consultation des textes de loi...',
  'Analyse de la jurisprudence...',
  'Rédaction de la réponse...',
];

const CREATIVE_STEPS_FALLBACK = [
  'Analyse du brief créatif...',
  'Recherche des campagnes conformes...',
  'Consultation du cadre légal...',
  'Brainstorm des territoires créatifs...',
  'Exploration des angles originaux...',
  'Rédaction des slogans et concepts...',
  'Validation juridique de chaque idée...',
  'Scoring de conformité...',
];

export default function Loader({ analysisType, progress = null, progressLabel = null }: Props) {
  // Comparaisons alignées sur les labels d'ANALYSIS_OPTIONS (shared/types.ts).
  const isQuestion = analysisType === 'Question réglementaire';
  const isCreative = analysisType === 'Visaly Créa';
  const fallbackSteps = isCreative
    ? CREATIVE_STEPS_FALLBACK
    : isQuestion
    ? QUESTION_STEPS_FALLBACK
    : ANALYSIS_STEPS_FALLBACK;
  const [currentStep, setCurrentStep] = useState(0);

  // Animation legacy : tourne uniquement quand on n'a pas encore reçu de progression SSE.
  useEffect(() => {
    if (progress !== null) return;
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % fallbackSteps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [fallbackSteps.length, progress]);

  // ── Jauge « trickle » ────────────────────────────────────
  // Le backend remonte la progression par paliers (ex. 38 puis 80). Entre deux
  // paliers, on fait grimper la barre en asymptote vers un plafond (real+40,
  // borné à 95) pour montrer que l'IA cogite — sans jamais atteindre 100 avant
  // le vrai 'done'. On saute toujours en avant quand un vrai palier arrive.
  const [displayed, setDisplayed] = useState<number | null>(null);
  const realRef = useRef(0);
  const started = progress !== null;

  useEffect(() => {
    if (progress === null) { setDisplayed(null); return; }
    realRef.current = Math.max(0, Math.min(100, progress));
    setDisplayed((d) => Math.max(d ?? 0, realRef.current));
  }, [progress]);

  useEffect(() => {
    if (!started) return;
    const id = setInterval(() => {
      setDisplayed((d) => {
        const real = realRef.current;
        if (real >= 100) return 100;
        const cur = d ?? real;
        // Deux régimes : phase « juges » (mid) → on couvre gentiment le gros
        // saut 38→80 ; phase « arbitrage » (real ≥ 70, qui dure 1-2 min) → on
        // avance TRÈS lentement en asymptote vers 95, pour un mouvement continu
        // qui ne donne jamais l'impression d'être figé près de 100.
        const isLate = real >= 70;
        const cap = isLate ? 95 : Math.min(real + 38, 90);
        const factor = isLate ? 0.006 : 0.03;
        if (cur >= cap) return cur;
        return cur + (cap - cur) * factor;
      });
    }, 250);
    return () => clearInterval(id);
  }, [started]);

  // Affichage : on privilégie l'étape backend si dispo, sinon fallback animé.
  const liveLabel = progressLabel ?? fallbackSteps[currentStep];
  const pct = displayed;

  return (
    /* Panneau teinté par la typologie : pendant l'attente, la couleur reste le
       seul repère de « où l'on est » — le fond de page, lui, demeure crème. */
    <div className="card-tint animate-fade-in mx-auto flex max-w-2xl flex-col items-center justify-center rounded-3xl px-6 py-16">
      {/* Une analyse dure une à deux minutes : c'est le plus long face-à-face
          avec un seul écran. On y montre l'anneau du score final lui-même —
          vague de couleur tant que le backend n'a rien dit, puis remplissage
          arc par arc au rythme de la progression SSE. Le résultat arrive dans
          la forme qu'on a regardée se construire. */}
      <LoaderRing progress={pct} size={168} className="mb-8" />

      <h3 className="font-display text-lg text-slate-900 mb-2">
        Analyse &laquo;&nbsp;{analysisType}&nbsp;&raquo;
      </h3>

      <p className="h-5 text-sm text-slate-500 transition-opacity">
        {liveLabel}
      </p>

      <p className="mt-6 text-xs text-slate-400 max-w-sm text-center">
        {isCreative
          ? 'Notre IA explore des territoires créatifs puis les valide juridiquement. Merci de patienter, l\'analyse est en cours.'
          : isQuestion
          ? 'Notre IA recherche dans la base juridique et rédige la réponse. Merci de patienter, l\'analyse est en cours.'
          : 'L\'analyse mobilise nos 3 modèles d\'IA en séquence pour garantir un verdict équilibré et nuancé. Merci de patienter, l\'analyse est en cours.'}
      </p>

      <div className="mt-4 flex items-start gap-2 max-w-sm text-center mx-auto">
        <svg className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
        </svg>
        <p className="text-xs text-slate-400 text-left">
          Vous pouvez quitter cet écran : l'analyse continue en arrière-plan et vous serez notifié dès qu'elle est prête. Aucun crédit supplémentaire ne sera débité.
        </p>
      </div>
    </div>
  );
}
